/**
 * Voice AI — Shared Agent Styles
 * Common CSS for all agent sub-components (grid, detail, config, pipeline tabs).
 * Same pattern as settings-styles.js.
 */

import { css } from 'lit';

export const agentStyles = css`
  :host {
    display: block;
  }

  /* ─── Page Header ─── */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-8);
  }

  .page-header-text {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .page-title {
    font-family: var(--font-display);
    font-size: var(--text-4xl);
    font-weight: var(--font-black);
    color: var(--color-text-primary);
    letter-spacing: -0.04em;
  }

  .page-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  /* ─── View Toggle ─── */
  .view-toggle {
    display: flex;
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .view-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 34px;
    border: none;
    background: transparent;
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: all var(--transition-fast);
    font-size: var(--text-sm);
  }

  .view-toggle-btn:hover {
    color: var(--color-text-secondary);
    background: var(--color-bg-hover);
  }

  .view-toggle-btn.active {
    background: var(--color-bg-secondary);
    color: var(--color-accent);
    box-shadow: var(--shadow-xs);
  }

  /* ─── Agent Cards Grid ─── */
  .agents-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-6);
    animation: slideUp var(--duration-slow) var(--ease-out) both;
  }

  .agents-grid.list-view {
    grid-template-columns: 1fr;
  }

  .agents-grid.list-view .agent-card {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: var(--space-5);
    align-items: center;
  }

  .agents-grid.list-view .agent-card-body {
    display: flex;
    align-items: center;
    gap: var(--space-6);
  }

  .agents-grid.list-view .agent-stats-row {
    flex-direction: row;
  }

  .agents-grid.list-view .agent-card-footer {
    justify-content: flex-end;
  }

  .agent-card {
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    cursor: pointer;
    transition: all var(--transition-base);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    overflow: hidden;
  }

  .agent-card:hover {
    box-shadow: var(--shadow-hover-lift);
    border-color: var(--color-border-secondary);
    transform: scale(1.01);
  }

  /* Skeleton card — no interaction */
  .agent-card.sk-card {
    cursor: default;
    pointer-events: none;
  }

  .agent-card-header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
  }

  .agent-avatar {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-lg);
    color: var(--color-text-inverse);
    flex-shrink: 0;
  }

  .agent-avatar.color-orange { background: var(--color-primary); box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2); }
  .agent-avatar.color-blue { background: var(--color-accent); box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2); }
  .agent-avatar.color-green { background: var(--color-success); box-shadow: 0 2px 8px rgba(34, 197, 94, 0.2); }
  .agent-avatar.color-purple { background: var(--color-info); box-shadow: 0 2px 8px rgba(139, 92, 246, 0.2); }

  .agent-info {
    flex: 1;
    min-width: 0;
  }

  .agent-name {
    font-family: var(--font-display);
    font-size: var(--text-base);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
    line-height: var(--leading-tight);
    margin-bottom: var(--space-1);
  }

  .agent-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .agent-stats-row {
    display: flex;
    gap: var(--space-4);
    margin-bottom: var(--space-3);
  }

  .agent-stat-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
  }

  .stat-value {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
  }

  .stat-label {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    letter-spacing: var(--tracking-wide);
  }

  .agent-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: var(--space-3);
    border-top: 1px solid var(--color-border-subtle);
  }

  .last-active {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .voice-name {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-secondary);
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  /* ─── Agent Detail Panel ─── */
  .detail-panel {
    animation: slideUp var(--duration-slow) var(--ease-out) both;
  }

  .detail-back-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
    margin-bottom: var(--space-4);
  }

  .detail-back-btn:hover {
    color: var(--color-primary);
    background: var(--color-primary-light);
  }

  .detail-header {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    margin-bottom: var(--space-6);
  }

  .detail-avatar {
    width: 56px;
    height: 56px;
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xl);
    color: var(--color-text-inverse);
    flex-shrink: 0;
  }

  .detail-title-area {
    flex: 1;
  }

  .detail-title {
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
  }

  .detail-subtitle {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    margin-top: 2px;
  }

  /* ─── Tab Bar ─── */
  .detail-tabs {
    display: flex;
    gap: var(--space-1);
    border-bottom: 1px solid var(--color-border-primary);
    margin-bottom: var(--space-6);
  }

  .detail-tab {
    padding: var(--space-3) var(--space-4);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-tertiary);
    cursor: pointer;
    border: none;
    background: transparent;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: all var(--transition-fast);
    white-space: nowrap;
  }

  .detail-tab:hover {
    color: var(--color-text-secondary);
  }

  .detail-tab.active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
    background: var(--color-primary-light);
    border-radius: var(--radius-md) var(--radius-md) 0 0;
  }

  /* ─── Metrics Row ─── */
  .metrics-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-4);
    margin-bottom: var(--space-6);
  }

  .metric-card {
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    position: relative;
    overflow: hidden;
  }

  .metric-card .metric-label {
    margin-bottom: var(--space-2);
  }

  .metric-card .metric-value {
    font-size: var(--text-2xl);
  }

  /* ─── Glass Card (sections, recent calls) ─── */
  .glass-card,
  .recent-calls-section {
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    margin-bottom: var(--space-6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    position: relative;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-4);
  }

  /* ─── Call List ─── */
  .call-list-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) 0;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .call-list-item:last-child {
    border-bottom: none;
  }

  .call-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xs);
    flex-shrink: 0;
  }

  .call-icon-wrap.success {
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }

  .call-icon-wrap.missed {
    background: var(--color-error-bg);
    color: var(--color-error-text);
  }

  .call-info {
    flex: 1;
    min-width: 0;
  }

  .call-number {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
  }

  .call-time {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .call-duration {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-secondary);
  }

  /* ─── Activity Timeline ─── */
  .timeline {
    padding-left: var(--space-6);
    position: relative;
  }

  .timeline::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 4px;
    bottom: 4px;
    width: 2px;
    background: var(--color-border-primary);
  }

  .timeline-item {
    position: relative;
    padding-bottom: var(--space-4);
    padding-left: var(--space-4);
  }

  .timeline-item::before {
    content: '';
    position: absolute;
    left: -19px;
    top: 6px;
    width: 8px;
    height: 8px;
    border-radius: var(--radius-full);
    background: var(--color-primary);
    border: 2px solid var(--color-bg-primary);
  }

  .timeline-item:last-child {
    padding-bottom: 0;
  }

  .timeline-content {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
  }

  .timeline-time {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    margin-top: 2px;
  }

  /* ─── Config Sections ─── */
  .config-section {
    margin-bottom: var(--space-8);
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
    overflow: hidden;
  }

  .config-section-title {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
    margin-bottom: var(--space-1);
  }

  .config-section-desc {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    margin-bottom: var(--space-5);
  }

  /* Form base styles inherited from componentStyles (design-system.js) */

  .char-count {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    text-align: right;
    margin-top: 2px;
  }

  .input-with-btn {
    display: flex;
    gap: var(--space-2);
    align-items: center;
  }

  .input-with-btn .form-select,
  .input-with-btn .form-input {
    flex: 1;
  }

  .play-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-primary);
    background: var(--color-bg-secondary);
    color: var(--color-primary);
    cursor: pointer;
    transition: all var(--transition-fast);
    flex-shrink: 0;
  }

  .play-btn:hover {
    background: var(--color-primary-light);
    border-color: var(--color-primary);
  }

  /* ─── Business Hours ─── */
  .hours-grid {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .hours-row {
    display: grid;
    grid-template-columns: 48px 80px 1fr 1fr;
    gap: var(--space-3);
    align-items: center;
    padding: var(--space-2) var(--space-3);
    background: var(--color-bg-tertiary);
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: var(--text-sm);
  }

  .hours-day {
    font-weight: var(--font-medium);
    color: var(--color-text-primary);
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
  }

  .time-input {
    padding: var(--space-1\.5) var(--space-2);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-sm);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
  }

  .time-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-glow-primary);
  }

  /* ─── Sliders ─── */
  .slider-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .slider-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .slider-value {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-primary);
    background: var(--color-primary-light);
    padding: 2px var(--space-2);
    border-radius: var(--radius-sm);
  }

  input[type="range"] {
    -webkit-appearance: none;
    width: 100%;
    height: 6px;
    background: var(--color-bg-inset);
    border-radius: var(--radius-full);
    outline: none;
  }

  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border-radius: var(--radius-full);
    background: var(--color-primary);
    cursor: pointer;
    box-shadow: var(--shadow-sm);
    transition: all var(--transition-fast);
  }

  input[type="range"]::-webkit-slider-thumb:hover {
    transform: scale(1.15);
    box-shadow: var(--shadow-glow-primary);
  }

  /* ─── Toast ─── */
  .toast {
    position: fixed;
    bottom: var(--space-6);
    right: var(--space-6);
    background: var(--color-success);
    color: var(--color-text-inverse);
    padding: var(--space-3) var(--space-5);
    border-radius: var(--radius-lg);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    display: flex;
    align-items: center;
    gap: var(--space-2);
    box-shadow: var(--shadow-xl);
    z-index: var(--z-toast);
    transform: translateY(100px);
    opacity: 0;
    transition: all var(--duration-slow) var(--ease-out);
  }

  .toast.visible {
    transform: translateY(0);
    opacity: 1;
  }

  /* ─── Search & Filters ─── */
  .search-filters {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-6);
    flex-wrap: wrap;
  }

  .filter-pills {
    display: flex;
    gap: var(--space-1);
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    padding: 2px;
  }

  .filter-pill {
    padding: var(--space-1) var(--space-3);
    border: none;
    background: transparent;
    border-radius: var(--radius-sm);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: all var(--transition-fast);
    white-space: nowrap;
  }

  .filter-pill:hover {
    color: var(--color-text-secondary);
  }

  .filter-pill.active {
    background: var(--color-bg-secondary);
    color: var(--color-accent);
    box-shadow: var(--shadow-xs);
  }

  .results-count {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    margin-left: auto;
  }

  /* ─── Loading Skeleton (uses shared skeleton from design-system.js) ─── */

  /* ─── Data Source Badge ─── */
  .data-source-badge {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--color-text-tertiary);
    background: var(--color-bg-tertiary);
    padding: 2px var(--space-2);
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border-primary);
  }

  /* ─── Actions Footer ─── */
  .actions-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: var(--space-4);
    border-top: 1px solid var(--color-border-primary);
    margin-top: var(--space-4);
  }

  .actions-footer-end {
    display: flex;
    justify-content: flex-end;
    padding-top: var(--space-4);
  }

  /* ─── Responsive ─── */
  @media (max-width: 1200px) {
    .agents-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .metrics-row {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    .agents-grid {
      grid-template-columns: 1fr;
    }
    .metrics-row {
      grid-template-columns: 1fr;
    }
    .page-header {
      flex-wrap: wrap;
      gap: var(--space-3);
    }
    .activity-stats {
      grid-template-columns: 1fr;
    }
  }

  /* ═══════════════════════════════════════════════════════════
     ACTIVITY TAB — Call Log
     ═══════════════════════════════════════════════════════════ */

  /* Stats Row */
  .activity-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-4);
    margin-bottom: var(--space-5);
  }

  .activity-stat-card {
    background: var(--color-surface-primary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-lg);
    padding: var(--space-4);
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .activity-stat-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-lg);
    flex-shrink: 0;
  }

  .activity-stat-icon.calls {
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
  }

  .activity-stat-icon.duration {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }

  .activity-stat-icon.success {
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  }

  .activity-stat-value {
    font-size: var(--text-xl);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
    line-height: 1;
  }

  .activity-stat-label {
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    margin-top: 2px;
  }

  /* Call Log Table */
  .call-log-container {
    background: var(--color-surface-primary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-lg);
    overflow: hidden;
  }

  .call-log-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4) var(--space-5);
    border-bottom: 1px solid var(--color-border-primary);
  }

  .call-log-title {
    font-size: var(--text-base);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
  }

  .call-log-table {
    width: 100%;
    border-collapse: collapse;
  }

  .call-log-table thead th {
    padding: var(--space-3) var(--space-4);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: left;
    background: var(--color-surface-secondary);
    border-bottom: 1px solid var(--color-border-primary);
  }

  .call-log-table tbody tr {
    cursor: pointer;
    transition: background var(--duration-fast) var(--ease-out);
  }

  .call-log-table tbody tr:hover {
    background: var(--color-surface-hover);
  }

  .call-log-table tbody tr.expanded {
    background: var(--color-surface-secondary);
  }

  .call-log-table tbody td {
    padding: var(--space-3) var(--space-4);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    border-bottom: 1px solid var(--color-border-primary);
    vertical-align: middle;
  }

  .call-phone {
    font-family: var(--font-mono);
    font-weight: var(--font-medium);
    color: var(--color-text-primary);
  }

  .call-direction {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
  }

  .call-direction.inbound {
    color: #6366f1;
  }

  .call-direction.outbound {
    color: #10b981;
  }

  .call-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: var(--radius-full);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
  }

  .call-status-badge.completed {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }

  .call-status-badge.failed {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }

  .call-status-badge.active {
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
  }

  .call-tools-count {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .call-tools-count i {
    font-size: 11px;
  }

  /* Expanded Row — Transcript */
  .call-expand-row td {
    padding: 0 !important;
    border-bottom: 2px solid var(--color-border-primary);
  }

  .call-expand-content {
    padding: var(--space-4) var(--space-5);
    background: var(--color-surface-secondary);
  }

  .transcript-section-title {
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: var(--space-3);
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .transcript-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .transcript-msg {
    display: flex;
    flex-direction: column;
    max-width: 75%;
  }

  .transcript-msg.customer {
    align-self: flex-start;
  }

  .transcript-msg.agent {
    align-self: flex-end;
  }

  .transcript-bubble {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-lg);
    font-size: var(--text-sm);
    line-height: 1.5;
    color: var(--color-text-primary);
  }

  .transcript-msg.customer .transcript-bubble {
    background: var(--color-surface-primary);
    border: 1px solid var(--color-border-primary);
    border-bottom-left-radius: var(--radius-sm);
  }

  .transcript-msg.agent .transcript-bubble {
    background: rgba(99, 102, 241, 0.08);
    border: 1px solid rgba(99, 102, 241, 0.15);
    border-bottom-right-radius: var(--radius-sm);
  }

  .transcript-speaker {
    font-size: 10px;
    font-weight: var(--font-semibold);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-tertiary);
    margin-bottom: 2px;
    padding: 0 var(--space-1);
  }

  .transcript-msg.agent .transcript-speaker {
    text-align: right;
  }

  /* Tool Call Cards */
  .tool-call-card {
    background: var(--color-surface-primary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .tool-call-card.error {
    border-color: rgba(239, 68, 68, 0.3);
  }

  .tool-call-header {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    font-size: var(--text-xs);
    color: var(--color-text-secondary);
  }

  .tool-call-header i {
    color: var(--color-text-tertiary);
    font-size: 11px;
  }

  .tool-call-name {
    font-family: var(--font-mono);
    font-weight: var(--font-medium);
    color: var(--color-text-primary);
  }

  .tool-call-status {
    padding: 1px 6px;
    border-radius: var(--radius-full);
    font-size: 10px;
    font-weight: var(--font-semibold);
  }

  .tool-call-status.success {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }

  .tool-call-status.error {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }

  .tool-call-time {
    color: var(--color-text-disabled);
    font-family: var(--font-mono);
    margin-left: auto;
  }

  .tool-call-body {
    border-top: 1px solid var(--color-border-primary);
  }

  .tool-call-error {
    padding: var(--space-2) var(--space-3);
    background: rgba(239, 68, 68, 0.06);
    color: #ef4444;
    font-size: var(--text-xs);
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
  }

  .tool-call-error i {
    margin-top: 1px;
    flex-shrink: 0;
  }

  .tool-call-detail-section {
    padding: var(--space-2) var(--space-3);
    border-top: 1px solid var(--color-border-primary);
  }

  .tool-call-detail-label {
    font-size: 10px;
    font-weight: var(--font-semibold);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-tertiary);
    margin-bottom: var(--space-1);
  }

  .tool-call-json {
    font-family: var(--font-mono);
    font-size: 11px;
    line-height: 1.5;
    color: var(--color-text-secondary);
    background: var(--color-surface-secondary);
    padding: var(--space-2);
    border-radius: var(--radius-sm);
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 200px;
    overflow-y: auto;
  }

  .tool-calls-section {
    margin-top: var(--space-4);
    padding-top: var(--space-4);
    border-top: 1px solid var(--color-border-primary);
  }

  .tool-calls-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  /* Loading & Empty States */
  .activity-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-10);
    color: var(--color-text-tertiary);
    font-size: var(--text-sm);
    gap: var(--space-2);
  }

  .activity-loading i {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Pagination */
  .activity-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-3) var(--space-5);
    border-top: 1px solid var(--color-border-primary);
  }

  .activity-pagination-info {
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .activity-pagination-controls {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .activity-page-btn {
    padding: var(--space-1) var(--space-2);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    background: var(--color-surface-primary);
    color: var(--color-text-secondary);
    font-size: var(--text-xs);
    cursor: pointer;
    min-width: 28px;
    text-align: center;
    transition: all var(--duration-fast) var(--ease-out);
  }

  .activity-page-btn:hover:not(:disabled) {
    background: var(--color-surface-hover);
    color: var(--color-text-primary);
  }

  .activity-page-btn.active {
    background: var(--color-accent);
    border-color: var(--color-accent);
    color: #fff;
  }

  .activity-page-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .detail-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-6);
    color: var(--color-text-tertiary);
    font-size: var(--text-xs);
    gap: var(--space-2);
  }

  .no-transcript {
    padding: var(--space-4);
    text-align: center;
    color: var(--color-text-tertiary);
    font-size: var(--text-sm);
    font-style: italic;
  }

  /* Activity Skeleton */
  /* skeleton-row uses shared styles from design-system.js */
  .skeleton-row:last-child {
    border-top: none;
  }

  /* ═══════════════════════════════════════════════════════════
     VOICE TAB — Voice Selection Grid
     ═══════════════════════════════════════════════════════════ */

  /* Filter Bar */
  .voice-filter-bar {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
    margin-bottom: var(--space-5);
    padding-bottom: var(--space-4);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .voice-filter-bar .form-select {
    width: auto;
  }

  .voice-gender-check {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    cursor: pointer;
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    user-select: none;
    transition: color var(--transition-fast);
  }

  .voice-gender-check:hover {
    color: var(--color-text-primary);
  }

  .voice-checkbox {
    width: 22px;
    height: 22px;
    border: 1.5px solid var(--color-border-primary);
    border-radius: var(--radius-sm);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: transparent;
    background: var(--color-bg-secondary);
    transition: all var(--transition-fast);
    flex-shrink: 0;
  }

  .voice-checkbox.checked {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }

  .voice-clear-btn {
    padding: var(--space-1) var(--space-3);
    background: transparent;
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    color: var(--color-text-tertiary);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    cursor: pointer;
    transition: all var(--transition-fast);
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
  }

  .voice-clear-btn:hover {
    color: var(--color-error);
    border-color: var(--color-error);
    background: rgba(239, 68, 68, 0.05);
  }

  .voice-count {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    margin-left: auto;
  }

  /* Voice Grid */
  .voice-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-4);
  }

  /* Voice Card */
  .voice-card {
    background: var(--color-bg-secondary);
    border: 1.5px solid var(--color-border-primary);
    border-radius: var(--radius-lg);
    padding: var(--space-4);
    cursor: pointer;
    transition: all var(--transition-base);
    position: relative;
    overflow: hidden;
  }

  .voice-card:hover {
    border-color: var(--color-border-secondary);
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }

  .voice-card.selected {
    border-color: var(--color-primary);
    background: var(--color-primary-light);
    box-shadow: 0 0 0 1px var(--color-primary), var(--shadow-sm);
  }

  .voice-card.playing {
    border-color: var(--color-accent);
  }

  .voice-card.sk-card {
    cursor: default;
    pointer-events: none;
  }

  .voice-card-header {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .voice-card .voice-avatar {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-sm);
    font-weight: var(--font-bold);
    color: #fff;
    flex-shrink: 0;
    overflow: hidden;
  }

  .voice-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--radius-full);
  }

  .voice-card-info {
    flex: 1;
    min-width: 0;
  }

  .voice-card-name {
    font-family: var(--font-display);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
    line-height: 1.2;
  }

  .voice-card-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    margin-top: 2px;
  }

  .voice-card-gender {
    text-transform: capitalize;
  }

  .voice-card-id {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--color-text-disabled);
    margin-top: var(--space-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Play Button */
  .voice-card .voice-play-btn {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-full);
    border: 1px solid var(--color-border-primary);
    background: var(--color-bg-primary);
    color: var(--color-text-tertiary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    cursor: pointer;
    transition: all var(--transition-fast);
    flex-shrink: 0;
  }

  .voice-card .voice-play-btn:hover {
    background: var(--color-primary-light);
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  .voice-card .voice-play-btn.playing {
    background: var(--color-accent);
    border-color: var(--color-accent);
    color: #fff;
    animation: voicePulse 1.5s ease-in-out infinite;
  }

  @keyframes voicePulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.3); }
    50% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0); }
  }

  /* Tags */
  .voice-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: var(--space-2);
  }

  .voice-tag {
    display: inline-block;
    padding: 1px var(--space-2);
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-full);
    font-family: var(--font-body);
    font-size: 10px;
    color: var(--color-text-tertiary);
    white-space: nowrap;
  }

  .voice-card.selected .voice-tag {
    background: rgba(245, 158, 11, 0.08);
    border-color: rgba(245, 158, 11, 0.2);
    color: var(--color-primary);
  }

  /* Selected Badge */
  .voice-selected-badge {
    position: absolute;
    top: var(--space-2);
    right: var(--space-2);
    width: 22px;
    height: 22px;
    border-radius: var(--radius-full);
    background: var(--color-primary);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    box-shadow: var(--shadow-sm);
  }

  /* Load More */
  .voice-load-more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-3);
    margin-top: var(--space-5);
    padding-top: var(--space-4);
    border-top: 1px solid var(--color-border-subtle);
  }

  .voice-load-more-btn {
    padding: var(--space-2) var(--space-5);
  }

  .voice-load-more-info {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  /* Empty & Error */
  .voice-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-10);
    color: var(--color-text-tertiary);
    font-size: var(--text-sm);
    text-align: center;
    gap: var(--space-2);
  }

  .voice-error {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    background: rgba(239, 68, 68, 0.06);
    border: 1px solid rgba(239, 68, 68, 0.15);
    border-radius: var(--radius-md);
    color: var(--color-error);
    font-size: var(--text-sm);
    margin-bottom: var(--space-4);
  }

  /* Responsive Voice Grid */
  @media (max-width: 1200px) {
    .voice-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (max-width: 900px) {
    .voice-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 600px) {
    .voice-grid {
      grid-template-columns: 1fr;
    }
    .voice-filter-bar {
      flex-direction: column;
      align-items: stretch;
    }
    .voice-count {
      margin-left: 0;
    }
  }
`;
